import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { logger } from './LoggerService';

/**
 * Hook that logs route changes for debugging purposes.
 *
 * Features:
 * - Logs pathname and search params on navigation
 * - Tracks previous pathname for transition logging
 * - Skips logging on initial mount (optional)
 *
 * @param skipInitialMount - If true, doesn't log the initial route (default: true)
 */
export function useNavigationLogger(skipInitialMount = true): void {
  const location = useLocation();
  const previousPathnameRef = useRef<string | null>(null);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    // Skip initial mount if configured
    if (skipInitialMount && isInitialMountRef.current) {
      isInitialMountRef.current = false;
      previousPathnameRef.current = location.pathname;
      return;
    }

    // Only log if pathname actually changed
    if (previousPathnameRef.current !== location.pathname) {
      logger.logNavigation({
        pathname: location.pathname,
        search: location.search || undefined,
        previousPathname: previousPathnameRef.current || undefined,
      });

      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname, location.search, skipInitialMount]);
}
